import suncalc from 'suncalc';

const {round, trunc: truncate} = Math;

enum EMoonPhase {
  New = 0,
  WaxingCrescent,
  QuarterMoon,
  WaxingGibbous,
  Full,
  WaningGibbous,
  LastQuarter,
  WaningCrescent,
  COUNT = 8,
}

const moonIcons: [
  New: string,
  WaxingCrescent: string,
  QuarterMoon: string,
  WaxingGibbous: string,
  Full: string,
  WaningGibbous: string,
  LastQuarter: string,
  WaningCrescent: string,
] = ['🌑', '🌒', '🌓', '🌔', '🌕', '🌖', '🌗', '🌘'];

enum EMoonPhaseName {
  New = 'New',
  WaxingCrescent = 'Waxing Crescent',
  QuarterMoon = 'Quarter Moon',
  WaxingGibbous = 'Waxing Gibbous',
  Full = 'Full',
  WaningGibbous = 'Waning Gibbous',
  LastQuarter = 'Last Quarter',
  WaningCrescent = 'Waning Crescent',
  COUNT = 'COUNT',
}

const MOON_PHASE_NAMES: EMoonPhaseName[] = [
  // Look-up table.
  EMoonPhaseName.New,
  EMoonPhaseName.WaxingCrescent,
  EMoonPhaseName.QuarterMoon,
  EMoonPhaseName.WaxingGibbous,
  EMoonPhaseName.Full,
  EMoonPhaseName.WaningGibbous,
  EMoonPhaseName.LastQuarter,
  EMoonPhaseName.WaningCrescent,
  EMoonPhaseName.COUNT,
];

// Mean length of a synodic month: 29 days, 12 hours, 44 minutes, 3 seconds.
// Reference: http://individual.utoronto.ca/kalendis/lunar/#FALC
// Only the legacy `moonPhase` helpers below still use it. The phase *times* no
// longer come from a mean cycle — see `phaseJDE`.
const LUNAR_CYCLE = 29.5305882;

const DAYS_PER_YEAR = 365.25;
const DAYS_PER_MONTH = 30.6;

// Number of days since known new moon on `1900-01-01`.
const DAYS_SINCE_NEW_MOON_1900_01_01 = 694039.09;

interface IResult {
  name: EMoonPhaseName;
  phase: EMoonPhase;
  icon: string;
}

/**
 * Legacy day-resolution phase estimate, ported from
 * `http://www.voidware.com/moon_phase.htm`. Kept because it is exported, but it
 * is a mean-cycle approximation and nothing in the apps calls it — prefer
 * `getLunarPhase`, which is built on real phase instants.
 */
function moonPhaseAlt(date: Date = new Date()): IResult {
  const year: number = date.getFullYear();

  const month: number = date.getMonth() + 1;
  const day: number = date.getDate();
  return moonPhase(year, month, day);
}

function moonPhase(year: number, month: number, day: number): IResult {
  if (month < 3) {
    year--;
    month += 12;
  }

  month += 1;

  let totalDaysElapsed: number =
    DAYS_PER_YEAR * year +
    DAYS_PER_MONTH * month +
    day -
    DAYS_SINCE_NEW_MOON_1900_01_01;

  totalDaysElapsed /= LUNAR_CYCLE; // Divide by the lunar cycle.

  let phase: number = truncate(totalDaysElapsed);

  /*
      Subtract integer part to leave fractional part of original
      `totalDaysElapsed`.
     */
  totalDaysElapsed -= phase;

  // Scale fraction from `0-8`.
  phase = round(totalDaysElapsed * 8);
  if (phase >= 8) {
    phase = 0; // `0` and `8` are the same so turn `8` into `0`.
  }

  if (phase >= EMoonPhase.COUNT || phase < EMoonPhase.New) {
    throw new Error(`Invalid moon phase: ${phase}`);
  }

  return {phase, name: MOON_PHASE_NAMES[phase], icon: moonIcons[phase]};
}

/* -------------------------------------------------------------------------- *
 * Principal phase times — Meeus, "Astronomical Algorithms" 2nd ed., ch. 49.
 *
 * This replaces a model that placed every new and full moon on a constant
 * 29.530588853-day cycle counted from a fixed 1999 epoch. The real synodic month
 * swings roughly 29.27–29.83 days, so the mean model drifted up to 15 hours
 * either side of the truth: measured across the 365 dashboard-card anchors of
 * 2026, the *calendar day* it printed was wrong on a third of them. Worse, it
 * decided which lunation to report by comparing the mean age against
 * `LUNAR_MONTH / 2`, so on eight days of 2026 it skipped a whole cycle and named
 * a moon a month late — on 2026-08-27 it announced the next full moon as
 * 26 September with the real one eight hours away.
 *
 * The routine below reproduces the book's worked examples 49.a and 49.b to
 * within two seconds; `__tests__/utils/MoonPhaseTimes.test.ts` asserts both, and
 * cross-checks a further 40 lunations against suncalc's independent model.
 * -------------------------------------------------------------------------- */

const RAD = Math.PI / 180;
const sinDeg = (degrees: number) => Math.sin(degrees * RAD);
const cosDeg = (degrees: number) => Math.cos(degrees * RAD);

const UNIX_EPOCH_JD = 2440587.5;
const MS_PER_DAY = 86400000;

/** Quarter of the cycle: 0 new, 0.25 first quarter, 0.5 full, 0.75 last quarter. */
type Quarter = 0 | 0.25 | 0.5 | 0.75;

const PRINCIPAL_QUARTER: Partial<Record<EMoonPhase, Quarter>> = {
  [EMoonPhase.New]: 0,
  [EMoonPhase.QuarterMoon]: 0.25,
  [EMoonPhase.Full]: 0.5,
  [EMoonPhase.LastQuarter]: 0.75,
};

// Periodic terms for the new and full moons (Meeus 49, pp. 351-352). The two
// series differ only in their first seven amplitudes and are otherwise identical,
// so they share one table rather than being transcribed twice. Each row is
// `[newMoonAmplitude, fullMoonAmplitude, eccentricityPower, m, mp, f]`, and the
// last three are the multipliers of M (sun), M' (moon) and F in the argument.
// The `-0.00017 sin Ω` term common to both series is added after the loop.
const SYZYGY_TERMS: [number, number, number, number, number, number][] = [
  // newAmp,  fullAmp, ePower, m, mp, f
  [-0.4072, -0.40614, 0, 0, 1, 0],
  [0.17241, 0.17302, 1, 1, 0, 0],
  [0.01608, 0.01614, 0, 0, 2, 0],
  [0.01039, 0.01043, 0, 0, 0, 2],
  [0.00739, 0.00734, 1, -1, 1, 0],
  [-0.00514, -0.00515, 1, 1, 1, 0],
  [0.00208, 0.00209, 2, 2, 0, 0],
  [-0.00111, -0.00111, 0, 0, 1, -2],
  [-0.00057, -0.00057, 0, 0, 1, 2],
  [0.00056, 0.00056, 1, 1, 2, 0],
  [-0.00042, -0.00042, 0, 0, 3, 0],
  [0.00042, 0.00042, 1, 1, 0, 2],
  [0.00038, 0.00038, 1, 1, 0, -2],
  [-0.00024, -0.00024, 1, -1, 2, 0],
  [-0.00007, -0.00007, 0, 2, 1, 0],
  [0.00004, 0.00004, 0, 0, 2, -2],
  [0.00004, 0.00004, 0, 3, 0, 0],
  [0.00003, 0.00003, 0, 1, 1, -2],
  [0.00003, 0.00003, 0, 0, 2, 2],
  [-0.00003, -0.00003, 0, 1, 1, 2],
  [0.00003, 0.00003, 0, -1, 1, 2],
  [-0.00002, -0.00002, 0, -1, 1, -2],
  [-0.00002, -0.00002, 0, 1, 3, 0],
  [0.00002, 0.00002, 0, 0, 4, 0],
];

// Periodic terms for the quarters (Meeus 49, p. 352): `[amplitude, ePower, m, mp, f]`,
// read the same way as the table above.
const QUARTER_TERMS: [number, number, number, number, number][] = [
  [-0.62801, 0, 0, 1, 0],
  [0.17172, 1, 1, 0, 0],
  [-0.01183, 1, 1, 1, 0],
  [0.00862, 0, 0, 2, 0],
  [0.00804, 0, 0, 0, 2],
  [0.00454, 1, -1, 1, 0],
  [0.00204, 2, 2, 0, 0],
  [-0.0018, 0, 0, 1, -2],
  [-0.0007, 0, 0, 1, 2],
  [-0.0004, 0, 0, 3, 0],
  [-0.00034, 1, -1, 2, 0],
  [0.00032, 1, 1, 0, 2],
  [0.00032, 1, 1, 0, -2],
  [-0.00028, 2, 2, 1, 0],
  [0.00027, 1, 1, 2, 0],
  [-0.00005, 0, -1, 1, -2],
  [0.00004, 0, 0, 2, 2],
  [-0.00004, 0, 1, 1, 2],
  [0.00004, 0, -2, 1, 0],
  [0.00003, 0, 1, 1, -2],
  [0.00003, 0, 3, 0, 0],
  [0.00002, 0, 0, 2, -2],
  [0.00002, 0, -1, 1, 2],
  [-0.00002, 0, 1, 3, 0],
];

// Fourteen further corrections from planetary arguments (Meeus 49, p. 352),
// as `[amplitude, constant, kCoefficient]`.
const PLANETARY_TERMS: [number, number, number][] = [
  [0.000325, 299.77, 0.107408],
  [0.000165, 251.88, 0.016321],
  [0.000164, 251.83, 26.651886],
  [0.000126, 349.42, 36.412478],
  [0.00011, 84.66, 18.206239],
  [0.000062, 141.74, 53.303771],
  [0.00006, 207.14, 2.453732],
  [0.000056, 154.84, 7.30686],
  [0.000047, 34.52, 27.261239],
  [0.000042, 207.19, 0.121824],
  [0.00004, 291.34, 1.844379],
  [0.000037, 161.72, 24.198154],
  [0.000035, 239.56, 25.513099],
  [0.000023, 331.55, 3.592518],
];

/**
 * Julian Ephemeris Day of the phase for lunation index `k`, in Dynamical Time.
 * Integer `k` is a new moon; add 0.25, 0.5 or 0.75 for the quarters, full moon
 * and last quarter. `k = 0` is the new moon of 2000 January 6.
 */
const phaseJDE = (k: number): number => {
  const quarter = k - Math.floor(k);
  const T = k / 1236.85;
  const T2 = T * T;
  const T3 = T2 * T;
  const T4 = T3 * T;

  const mean =
    2451550.09766 +
    29.530588861 * k +
    0.00015437 * T2 -
    0.00000015 * T3 +
    0.00000000073 * T4;

  // Eccentricity of the Earth's orbit, which slowly decreases (Meeus 47.6).
  const E = 1 - 0.002516 * T - 0.0000074 * T2;
  const M = 2.5534 + 29.1053567 * k - 0.0000014 * T2 - 0.00000011 * T3;
  const Mp =
    201.5643 +
    385.81693528 * k +
    0.0107582 * T2 +
    0.00001238 * T3 -
    0.000000058 * T4;
  const F =
    160.7108 +
    390.67050284 * k -
    0.0016118 * T2 -
    0.00000227 * T3 +
    0.000000011 * T4;
  const omega = 124.7746 - 1.56375588 * k + 0.0020672 * T2 + 0.00000215 * T3;

  let correction = 0;

  if (quarter === 0 || quarter === 0.5) {
    const amplitudeIndex = quarter === 0 ? 0 : 1;
    for (const term of SYZYGY_TERMS) {
      const [, , ePower, m, mp, f] = term;
      correction +=
        term[amplitudeIndex] * E ** ePower * sinDeg(m * M + mp * Mp + f * F);
    }
  } else {
    for (const [amplitude, ePower, m, mp, f] of QUARTER_TERMS) {
      correction += amplitude * E ** ePower * sinDeg(m * M + mp * Mp + f * F);
    }
    // The quarters carry one extra term, added at first quarter and subtracted
    // at last quarter (Meeus 49, p. 352).
    const W =
      0.00306 -
      0.00038 * E * cosDeg(M) +
      0.00026 * cosDeg(Mp) -
      0.00002 * cosDeg(Mp - M) +
      0.00002 * cosDeg(Mp + M) +
      0.00002 * cosDeg(2 * F);
    correction += quarter === 0.25 ? W : -W;
  }

  // Common to all three series with the same coefficient, so it sits outside them.
  correction += -0.00017 * sinDeg(omega);

  for (const [amplitude, constant, kCoefficient] of PLANETARY_TERMS) {
    correction += amplitude * sinDeg(constant + kCoefficient * k);
  }

  return mean + correction;
};

/**
 * Difference between Dynamical Time and UT, in seconds. Espenak & Meeus's
 * polynomial for 2005-2050; outside that span it stays inside a minute for the
 * whole 20th and 21st centuries, which is far below anything this module claims.
 */
const deltaTSeconds = (year: number): number => {
  const t = year - 2000;
  return 62.92 + 0.32217 * t + 0.005589 * t * t;
};

/** Unix seconds (UT) of the phase for lunation index `k`. */
const phaseTime = (k: number): number => {
  const jde = phaseJDE(k);
  const utcMs = (jde - UNIX_EPOCH_JD) * MS_PER_DAY;
  const year = new Date(utcMs).getUTCFullYear();
  return (utcMs - deltaTSeconds(year) * 1000) / 1000;
};

/**
 * Approximate lunation index for a date, per Meeus: `k ≈ (year - 2000) × 12.3685`
 * with the year expressed as a decimal. Only ever used as a starting guess — the
 * callers below step it until the bracketing is exact.
 */
const approximateLunation = (date: Date): number => {
  const year = date.getUTCFullYear();
  const yearStart = Date.UTC(year, 0, 1);
  const nextYearStart = Date.UTC(year + 1, 0, 1);
  const decimalYear =
    year + (date.getTime() - yearStart) / (nextYearStart - yearStart);
  return (decimalYear - 2000) * 12.3685;
};

/** Lunation index of the requested quarter nearest to `date`, as a starting point. */
const nearestIndex = (date: Date, quarter: Quarter): number =>
  Math.round(approximateLunation(date) - quarter) + quarter;

/** Unix seconds of the first occurrence of `quarter` strictly after `date`. */
const phaseTimeAfter = (date: Date, quarter: Quarter): number => {
  const ms = date.getTime();
  let k = nearestIndex(date, quarter);
  // The guess is never more than one lunation out, so each loop runs at most twice.
  while (phaseTime(k) * 1000 > ms) {
    k -= 1;
  }
  while (phaseTime(k) * 1000 <= ms) {
    k += 1;
  }
  return phaseTime(k);
};

/**
 * Index of the lunation containing `date` — the one whose new moon is the last to
 * precede it. Everything that describes *where in the cycle* a date sits is derived
 * from this single index, so age, fraction and phase name can never disagree about
 * which lunation they are talking about.
 */
const lunationIndex = (date: Date): number => {
  const ms = date.getTime();
  let k = Math.round(approximateLunation(date));
  while (phaseTime(k) * 1000 > ms) {
    k -= 1;
  }
  while (phaseTime(k + 1) * 1000 <= ms) {
    k += 1;
  }
  return k;
};

/**
 * Days elapsed since the new moon that precedes `date` — the moon's age, as an
 * observer would count it. Runs 0 at new moon to roughly 29.5 just before the
 * next one; the exact ceiling is that lunation's own length, not a mean.
 */
const getLunarAge = (date: Date = new Date()): number =>
  (date.getTime() / 1000 - phaseTime(lunationIndex(date))) / 86400;

/**
 * Position within the current lunation, 0 at the new moon that started it and
 * approaching 1 at the next. Unlike a mean-cycle fraction this is anchored to
 * both real endpoints, so it stays in step with the sky through a short lunation
 * and a long one alike.
 */
const getLunationFraction = (date: Date = new Date()): number => {
  const k = lunationIndex(date);
  const previousNew = phaseTime(k);
  const nextNew = phaseTime(k + 1);
  return (date.getTime() / 1000 - previousNew) / (nextNew - previousNew);
};

/**
 * How close to a principal instant a date has to be for that name to win. Half a
 * day either side, so exactly one calendar day carries "Full moon" rather than
 * two or none — and, because the dashboard evaluates this at local noon, the day
 * it lands on is the day the phase actually happens.
 */
const PRINCIPAL_WINDOW_MS = 12 * 3600 * 1000;

const getLunarPhase = (date: Date = new Date()): EMoonPhase => {
  const ms = date.getTime();
  const k = lunationIndex(date);

  // The five principal instants that bound this lunation, including the new moon
  // that closes it — a date in the cycle's final hours is named "New", not
  // "Waning crescent". Taken from one index rather than from separate forward
  // searches: searching forward from the date skips any instant it has just
  // passed, which silently drops the name for the half-day after every quarter.
  const instants: [EMoonPhase, number][] = [
    [EMoonPhase.New, phaseTime(k)],
    [EMoonPhase.QuarterMoon, phaseTime(k + 0.25)],
    [EMoonPhase.Full, phaseTime(k + 0.5)],
    [EMoonPhase.LastQuarter, phaseTime(k + 0.75)],
    [EMoonPhase.New, phaseTime(k + 1)],
  ];

  for (const [phase, unix] of instants) {
    if (Math.abs(ms - unix * 1000) < PRINCIPAL_WINDOW_MS) {
      return phase;
    }
  }

  const fraction = getLunationFraction(date);
  if (fraction < 0.25) {
    return EMoonPhase.WaxingCrescent;
  }
  if (fraction < 0.5) {
    return EMoonPhase.WaxingGibbous;
  }
  if (fraction < 0.75) {
    return EMoonPhase.WaningGibbous;
  }
  return EMoonPhase.WaningCrescent;
};

/**
 * Unix seconds of the next occurrence of a principal phase after `date`.
 *
 * Only the four principal phases have an instant: a moon is "waxing crescent"
 * for a week, so there is no moment to return. Callers asking for one have a bug
 * rather than an edge case, so this throws instead of quietly returning `date`,
 * which is what the previous implementation did.
 */
const getNextPhaseTimestamp = (date: Date, phase: EMoonPhase): number => {
  const quarter = PRINCIPAL_QUARTER[phase];
  if (quarter === undefined) {
    throw new Error(
      `getNextPhaseTimestamp: ${EMoonPhase[phase] ?? phase} is not a principal phase. ` +
        'Pass New, QuarterMoon, Full or LastQuarter.',
    );
  }
  return Math.round(phaseTimeAfter(date, quarter));
};

/* -------------------------------------------------------------------------- *
 * Per-location moon data — illumination, position, tonight's pass, dark sky.
 *
 * Shaped like `Sun.getInfo` so the two ephemeris helpers read the same way.
 * Positions come from suncalc; the phase *times* come from the Meeus routine
 * above, because suncalc has no phase-instant search and its own moon distance
 * is a single cosine term (never below 364 096 km against a real perigee near
 * 356 400 km) — accurate enough for an apparent size to ±2 %, not for a
 * supermoon claim.
 * -------------------------------------------------------------------------- */

const DEG = 180 / Math.PI;
const MOON_RADIUS_KM = 1737.4;

/** Astronomical twilight: the sun this far down is as dark as the sky gets. */
const ASTRONOMICAL_TWILIGHT_DEG = -18;

/**
 * Which shape the day takes. Mirrors `Sun.getRegime`'s vocabulary so the two
 * cards can branch the same way: above the Arctic Circle the moon spends days
 * at a time never setting or never rising, and a card that assumes a rise time
 * exists has no answer on those days.
 */
export type MoonRegime = 'normal' | 'alwaysUp' | 'alwaysDown';

export type MoonPosition = {
  altitude: number;
  azimuth: number;
  distance: number;
  apparentDiameter: number;
  tilt: number;
};

export type MoonInfo = MoonPosition & {
  fraction: number;
  phase: EMoonPhase;
  waxing: boolean;
  age: number;
  lunation: number;
  regime: MoonRegime;
  rise: number | null;
  set: number | null;
  /**
   * The most recent set at or before `timestamp`.
   *
   * `set` belongs to the pass being shown, which — when the moon is currently
   * down — is the *next* one, so it is a future time. Copy like "below the
   * horizon since ..." needs the set that already happened, and reaching for
   * `set` there prints a time that has not occurred yet.
   */
  lastSet: number | null;
  riseAzimuth: number | null;
  setAzimuth: number | null;
  transit: number | null;
  transitAltitude: number | null;
  transitAzimuth: number | null;
  alwaysUp: boolean;
  alwaysDown: boolean;
  darkWindow: {from: number; to: number} | null;
};

export type MoonInfoParams = {
  timestamp?: number;
  latitude: number;
  longitude: number;
};

const toBearing = (azimuthRadians: number) =>
  (((azimuthRadians * DEG + 180) % 360) + 360) % 360;

const altitudeAt = (unix: number, latitude: number, longitude: number) =>
  suncalc.getMoonPosition(new Date(unix * 1000), latitude, longitude).altitude *
  DEG;

const azimuthAt = (unix: number, latitude: number, longitude: number) =>
  toBearing(
    suncalc.getMoonPosition(new Date(unix * 1000), latitude, longitude).azimuth,
  );

/**
 * Position, size and tilt at one instant — no horizon search, no dark-sky scan.
 *
 * Split out because the card refreshes its "now" readout every minute, and
 * `getInfo` costs a culmination scan plus a 289-sample twilight sweep. The sun
 * card hit exactly this and had to memoise; see its comment about rebuilding 97
 * `getPosition` calls on every tick.
 */
const getPosition = ({
  timestamp,
  latitude,
  longitude,
}: MoonInfoParams): MoonPosition => {
  const date = new Date((timestamp ?? Math.floor(Date.now() / 1000)) * 1000);
  const position = suncalc.getMoonPosition(date, latitude, longitude);
  const illumination = suncalc.getMoonIllumination(date);

  return {
    altitude: position.altitude * DEG,
    /** Compass bearing from north, 0-360. suncalc measures from south. */
    azimuth: toBearing(position.azimuth),
    distance: position.distance,
    apparentDiameter:
      2 * Math.atan(MOON_RADIUS_KM / position.distance) * DEG * 60,
    /** Bright-limb angle measured from the zenith, in degrees. */
    tilt: (illumination.angle - position.parallacticAngle) * DEG,
  };
};

/**
 * Every horizon crossing suncalc can find around `unix`, in order.
 *
 * `getMoonTimes` answers per calendar day and returns rise and set in whatever
 * order they fall, so on most days the "set" it reports belongs to the previous
 * night's moon. Collecting four days of crossings and sorting them lets the
 * caller pick a rise and the set that actually follows it.
 */
const horizonCrossings = (
  unix: number,
  latitude: number,
  longitude: number,
) => {
  const crossings: {type: 'rise' | 'set'; at: number}[] = [];

  for (let day = -1; day <= 2; day += 1) {
    const times = suncalc.getMoonTimes(
      new Date((unix + day * 86400) * 1000),
      latitude,
      longitude,
      true,
    );
    if (times.rise) {
      crossings.push({
        type: 'rise',
        at: Math.floor(times.rise.getTime() / 1000),
      });
    }
    if (times.set) {
      crossings.push({type: 'set', at: Math.floor(times.set.getTime() / 1000)});
    }
  }

  const seen = new Set<string>();
  const unique = crossings
    .filter((c) => {
      const key = `${c.type}:${c.at}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.at - b.at);

  return unique;
};

/**
 * Whether the moon is up, down, or doing both around `unix`.
 *
 * Derived from sampled altitude rather than from suncalc's `alwaysUp` /
 * `alwaysDown`, which are not trustworthy: they are set from `ye`, the last
 * extremum its hourly interpolation happened to compute, not from the day's
 * altitude range. Measured at Tromsø, 2026-08-20 comes back `alwaysUp` with the
 * moon at -16.9°, and 2026-09-04 comes back `alwaysDown` with it at +19.3° —
 * the flags are frequently inverted, not merely imprecise.
 *
 * Sampled over one full lunar day (24 h 50 m) so a pass cannot hide between two
 * samples; the moon's altitude moves slowly enough that quarter-hour steps
 * resolve it.
 */
const LUNAR_DAY_SECONDS = 24 * 3600 + 50 * 60;

const regimeAround = (
  unix: number,
  latitude: number,
  longitude: number,
): MoonRegime => {
  let lowest = Infinity;
  let highest = -Infinity;

  for (
    let t = unix - LUNAR_DAY_SECONDS / 2;
    t <= unix + LUNAR_DAY_SECONDS / 2;
    t += 900
  ) {
    const altitude = altitudeAt(t, latitude, longitude);
    lowest = Math.min(lowest, altitude);
    highest = Math.max(highest, altitude);
  }

  if (highest < 0) {
    return 'alwaysDown';
  }
  return lowest > 0 ? 'alwaysUp' : 'normal';
};

/**
 * Highest point between two instants, refined to the second.
 *
 * Three passes rather than two. The altitude curve is almost flat at the top —
 * it changes by seconds of arc across a minute — so a ten-second grid can settle
 * either side of a minute boundary depending on where the coarse pass happened
 * to land, and the printed time flips between 02:27 and 02:28 for the same
 * night. Narrowing to one second costs about twenty extra samples and makes the
 * answer stable.
 */
const culmination = (
  from: number,
  to: number,
  latitude: number,
  longitude: number,
) => {
  let best = {at: from, altitude: altitudeAt(from, latitude, longitude)};

  const refine = (windowSeconds: number, step: number) => {
    const start = Math.max(from, best.at - windowSeconds);
    const end = Math.min(to, best.at + windowSeconds);
    for (let t = start; t <= end; t += step) {
      const altitude = altitudeAt(t, latitude, longitude);
      if (altitude > best.altitude) {
        best = {at: t, altitude};
      }
    }
  };

  const coarse = Math.max(60, Math.round((to - from) / 240));
  for (let t = from; t <= to; t += coarse) {
    const altitude = altitudeAt(t, latitude, longitude);
    if (altitude > best.altitude) {
      best = {at: t, altitude};
    }
  }
  refine(coarse, 10);
  refine(10, 1);

  return {at: Math.round(best.at), altitude: best.altitude};
};

/**
 * The longest stretch in the next 24 hours with the sun below astronomical
 * twilight and the moon below the horizon — the window in which deep-sky
 * observing and astrophotography are actually possible.
 */
const darkSkyWindow = (unix: number, latitude: number, longitude: number) => {
  const STEP = 300;
  let longest: {from: number; to: number} | null = null;
  let openedAt: number | null = null;
  let lastDarkAt: number | null = null;

  const close = () => {
    if (openedAt === null || lastDarkAt === null) {
      return;
    }
    const span = {from: openedAt, to: lastDarkAt};
    if (!longest || span.to - span.from > longest.to - longest.from) {
      longest = span;
    }
    openedAt = null;
    lastDarkAt = null;
  };

  for (let t = unix; t <= unix + 86400; t += STEP) {
    const sun =
      suncalc.getPosition(new Date(t * 1000), latitude, longitude).altitude *
      DEG;
    const dark =
      sun < ASTRONOMICAL_TWILIGHT_DEG && altitudeAt(t, latitude, longitude) < 0;

    if (dark) {
      if (openedAt === null) {
        openedAt = t;
      }
      // The window closes at the last sample that is *still* dark. Taking the
      // first non-dark sample instead overshoots by up to one step, which puts
      // the sun back above -18° inside a window we just called astronomical.
      lastDarkAt = t;
    } else {
      close();
    }
  }
  close();
  return longest;
};

export type MoonIllumination = {
  fraction: number;
  phase: EMoonPhase;
  waxing: boolean;
  age: number;
  lunation: number;
};

/**
 * The part of the moon's state that depends only on *when*.
 *
 * `getInfo` returns these alongside everything that needs a place — the pass,
 * the culmination, the dark-sky window — and computing those costs a horizon
 * scan plus a 289-sample twilight sweep. A caller that only wants "how much of
 * it is lit" should not pay for that, and more importantly should not have to
 * ask at the same instant.
 *
 * That distinction is the bug this exists for: the moon card sampled `getInfo`
 * at `now` because its rise/set logic must, and the detail screen sampled it at
 * local midday because the phase *name* must — so the two screens printed
 * 94.2 % and 94.8 % for the same moon, three hours apart. The lit fraction and
 * the age belong with the name, at midday; the pass belongs at `now`.
 */
const getIllumination = (timestamp?: number): MoonIllumination => {
  const date = new Date((timestamp ?? Math.floor(Date.now() / 1000)) * 1000);
  const illumination = suncalc.getMoonIllumination(date);
  return {
    fraction: illumination.fraction,
    phase: getLunarPhase(date),
    waxing: illumination.phase < 0.5,
    age: getLunarAge(date),
    lunation: getLunationFraction(date),
  };
};

const getInfo = ({
  timestamp,
  latitude,
  longitude,
}: MoonInfoParams): MoonInfo => {
  const unix = timestamp ?? Math.floor(Date.now() / 1000);

  const position = getPosition({timestamp: unix, latitude, longitude});
  const {altitude} = position;

  const crossings = horizonCrossings(unix, latitude, longitude);

  // While the moon is up, the pass on show is the one it is in the middle of —
  // not the next one. Jumping ahead at the moment of culmination would be a
  // card that changes its answer halfway through the night.
  let rise: number | null = null;
  let set: number | null = null;

  if (altitude > 0) {
    const previousRise = crossings
      .filter((c) => c.type === 'rise' && c.at <= unix)
      .pop();
    rise = previousRise ? previousRise.at : null;
    const nextSet = crossings.find((c) => c.type === 'set' && c.at > unix);
    set = nextSet ? nextSet.at : null;
  } else {
    const nextRise = crossings.find((c) => c.type === 'rise' && c.at > unix);
    rise = nextRise ? nextRise.at : null;
    const nextSet =
      rise === null
        ? null
        : crossings.find((c) => c.type === 'set' && c.at > (rise as number));
    set = nextSet ? nextSet.at : null;
  }

  const regime = regimeAround(unix, latitude, longitude);

  const previousSet = crossings
    .filter((c) => c.type === 'set' && c.at <= unix)
    .pop();
  const lastSet = previousSet ? previousSet.at : null;

  let transit: number | null = null;
  let transitAltitude: number | null = null;
  let transitAzimuth: number | null = null;
  if (rise !== null && set !== null) {
    const peak = culmination(rise, set, latitude, longitude);
    transit = peak.at;
    transitAltitude = peak.altitude;
    transitAzimuth = azimuthAt(peak.at, latitude, longitude);
  } else if (regime === 'alwaysUp') {
    const peak = culmination(
      unix,
      unix + LUNAR_DAY_SECONDS,
      latitude,
      longitude,
    );
    transit = peak.at;
    transitAltitude = peak.altitude;
    transitAzimuth = azimuthAt(peak.at, latitude, longitude);
  }

  return {
    ...position,
    ...getIllumination(unix),
    regime,
    rise,
    set,
    lastSet,
    riseAzimuth: rise === null ? null : azimuthAt(rise, latitude, longitude),
    setAzimuth: set === null ? null : azimuthAt(set, latitude, longitude),
    transit,
    transitAltitude,
    transitAzimuth,
    alwaysUp: regime === 'alwaysUp',
    alwaysDown: regime === 'alwaysDown',
    darkWindow: darkSkyWindow(unix, latitude, longitude),
  };
};

export {
  EMoonPhase,
  EMoonPhaseName,
  MOON_PHASE_NAMES,
  getIllumination,
  getLunarAge,
  getLunarPhase,
  getInfo,
  getLunationFraction,
  getPosition,
  getNextPhaseTimestamp,
  moonIcons,
  moonPhase,
  moonPhaseAlt,
};
